import { useEffect, useRef, useState } from "react";

import type { InjectionAnchor } from "@/hooks/use-injected-messages";
import { WELCOME_MESSAGES_SHOWN_PREFERENCE_KEY } from "@/hooks/use-onboarding-completion";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import type { Message } from "@/hooks/use-stream-conversation";
import { CONTENT_STORAGE_PREFIX } from "@/hooks/use-welcome-content";
import { useOrganization } from "@/queries/organization-queries";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

export type WelcomeAnchor = InjectionAnchor & {
    startCount: number;
    sessionId: string;
};

export const ANIMATED_KEY_PREFIX = "WELCOME_ANIMATED_";
const STORAGE_KEY_PREFIX = "WELCOME_ANCHORS_";

function getStoredAnchors(environmentId: string): WelcomeAnchor[] {
    try {
        const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${environmentId}`);
        return raw ? (JSON.parse(raw) as WelcomeAnchor[]) : [];
    } catch {
        return [];
    }
}

function storeAnchors(environmentId: string, anchors: WelcomeAnchor[]) {
    try {
        localStorage.setItem(`${STORAGE_KEY_PREFIX}${environmentId}`, JSON.stringify(anchors));
    } catch {
        // localStorage may be unavailable
    }
}

function removeLocalStorageByPrefix(prefix: string) {
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(prefix)) keysToRemove.push(key);
        }
        for (const key of keysToRemove) localStorage.removeItem(key);
    } catch {
        // localStorage may be unavailable
    }
}

/**
 * Removes persisted welcome anchors and animation progress for a specific environment.
 */
export function clearWelcomeAnchorsForEnvironment(environmentId: string) {
    try {
        localStorage.removeItem(`${STORAGE_KEY_PREFIX}${environmentId}`);
    } catch {
        // localStorage may be unavailable
    }
    removeLocalStorageByPrefix(ANIMATED_KEY_PREFIX);
    removeLocalStorageByPrefix(CONTENT_STORAGE_PREFIX);
}

/**
 * Removes all persisted welcome anchors, animation progress, and cached content from localStorage.
 */
export function clearWelcomeAnchors() {
    removeLocalStorageByPrefix(STORAGE_KEY_PREFIX);
    removeLocalStorageByPrefix(ANIMATED_KEY_PREFIX);
    removeLocalStorageByPrefix(CONTENT_STORAGE_PREFIX);
}

/**
 * Detects environment starts via startCount changes, snapshots the last message
 * ID as an anchor, and persists anchors to localStorage for page-refresh survival
 * and dedup. Determines eligibility internally based on org tier.
 */
export function useWelcomeAnchors(
    messages: Message[],
    startCount: number | undefined,
    environmentId: string | undefined,
    sessionId: string | undefined,
): WelcomeAnchor[] {
    const { data: organization } = useOrganization();
    const { value: welcomeMessagesShown, deleteValue: deleteWelcomeShown } = usePreferenceValue(
        WELCOME_MESSAGES_SHOWN_PREFERENCE_KEY,
    );
    const isEligibleForNew =
        !!organization &&
        welcomeMessagesShown === "false" &&
        (organization.tier === OrganizationTier.FREE_ONA || organization.tier === OrganizationTier.CORE);
    const [anchors, setAnchors] = useState<WelcomeAnchor[]>(() =>
        environmentId ? getStoredAnchors(environmentId) : [],
    );
    const prevSessionRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (
            !isEligibleForNew ||
            !environmentId ||
            !sessionId ||
            startCount === undefined ||
            startCount < 1 ||
            startCount > 3
        ) {
            return;
        }

        // Only create an anchor when this environment starts a new session.
        // This prevents anchors from being created when a *different*
        // environment increments the global startCount.
        if (sessionId === prevSessionRef.current) {
            return;
        }

        // Already have an anchor for this session (e.g., page reload)
        if (anchors.some((a) => a.sessionId === sessionId)) {
            prevSessionRef.current = sessionId;
            return;
        }

        // Defer until messages are available in the stream.
        if (messages.length === 0) {
            return;
        }

        prevSessionRef.current = sessionId;

        const lastMessage = messages[messages.length - 1];
        const newAnchor: WelcomeAnchor = {
            key: `welcome-${sessionId}`,
            startCount,
            sessionId,
            afterMessageId: lastMessage.id,
        };

        setAnchors((prev) => {
            const next = [...prev, newAnchor];
            storeAnchors(environmentId, next);
            return next;
        });
    }, [startCount, environmentId, sessionId, isEligibleForNew, messages, anchors, deleteWelcomeShown]);

    // Enterprise users should never see welcome messages, even stale ones.
    const isTierEligible =
        !!organization &&
        (organization.tier === OrganizationTier.FREE_ONA || organization.tier === OrganizationTier.CORE);
    if (!isTierEligible) {
        return [];
    }

    return anchors;
}
